<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {teamsBackend, type Team} from '@/backend/teams.backend';
import {useAuthStore} from '@/stores';
import AppPage from '@/components/AppPage.vue';
import RouteTab from '@/components/RouteTab.vue';
import {useToast} from 'primevue/usetoast';
import TeamPulseView from '@/components/pulse/TeamPulseView.vue';
import TeamEditView from '@/components/team/TeamEditView.vue';
import MetricsContent from '@/components/metrics/MetricsContent.vue';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const authStore = useAuthStore();
const team = ref<Team | null>(null);
const loading = ref(false);

const slug = computed(() => route.params.slug as string);
const PULSE_ROUTE = computed(() => `/teams/${slug.value}/pulse`);
const METRICS_ROUTE = computed(() => `/teams/${slug.value}/metrics`);
const EDIT_ROUTE = computed(() => `/teams/${slug.value}/edit`);

const tabs = computed(() => {
  const baseTabs = [
    {
      label: 'Pulse',
      to: PULSE_ROUTE.value,
      icon: 'pi pi-wave-pulse'
    },
    {
      label: 'Metrics',
      to: METRICS_ROUTE.value,
      icon: 'pi pi-chart-line'
    }
  ];
  
  // Add Edit tab only for admins
  if (authStore.isAdmin) {
    baseTabs.push({
      label: 'Edit',
      to: EDIT_ROUTE.value,
      icon: 'pi pi-pencil'
    });
  }
  
  return baseTabs;
});

onMounted(async () => {
  await loadTeam();
  pushToDefaultTab(route.path);
});

watch(() => route.params.slug, async () => {
  await loadTeam();
});

watch(() => route.path, (newPath) => {
  pushToDefaultTab(newPath);
});

async function loadTeam() {
  loading.value = true;
  try {
    team.value = await teamsBackend.getTeamBySlug(slug.value);
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load team details',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
}

function pushToDefaultTab(path: string) {
  // If we're on the base team route, redirect to pulse tab
  if (path === `/teams/${slug.value}`) {
    router.push(PULSE_ROUTE.value);
  }
}
</script>

<template>
  <AppPage :title="team?.name || 'Team Details'" :loaded="!loading" :tabs="tabs">
    <RouteTab v-if="team" :tab-route="PULSE_ROUTE">
      <TeamPulseView :team="team" />
    </RouteTab>
    <RouteTab v-if="team" :tab-route="METRICS_ROUTE">
      <MetricsContent :team="team" :show-department-filter="false" />
    </RouteTab>
    <RouteTab v-if="team && authStore.isAdmin" :tab-route="EDIT_ROUTE">
      <TeamEditView :team="team" @team-updated="loadTeam" />
    </RouteTab>
  </AppPage>
</template>
